import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlMatchResult, UrlSegment, mapToCanActivate } from '@angular/router';
import { LoginComponent } from './components/authentication/login/login.component';
import { LogoutComponent } from './components/authentication/logout/logout.component';
import { ResetPasswordComponent } from './components/authentication/reset-password/reset-password.component';
import { CryptoComponent } from './components/dashboard/crypto/crypto.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { NotFoundComponent } from './components/common/not-found/not-found.component';
import { AuthGuard } from './auth.service';
import { BrokerTabGuard } from './broker-tab.guard';
import { ModelsComponent } from './components/models/models.component';
import { GraficaModeloComponent } from './components/graficas/grafica-modelo/grafica-modelo.component';
import { GestorComponent } from './components/gestor/gestor.component';
import { DiversificadorComponent } from './components/diversificador/diversificador.component';
import { BrokerComponent } from './components/broker/broker.component';
import { AnalisisDataComponent } from './components/analisis-data/analisis-data.component';
import { RastreadorComponent } from './components/rastreador/rastreador.component';
import { SeguimientoCarterasComponent } from './components/seguimiento-carteras/seguimiento-carteras.component';
import { VernotificacionesComponent } from './components/vernotificaciones/vernotificaciones.component';
import { IboauthComponent } from './components/broker/login-dialog/iboauth/iboauth.component';
import { SuscripcionesConfirmarComponent } from './components/broker/suscripciones-confirmar/suscripciones-confirmar.component';
import { AdminComponent } from './components/admin/admin.component';
import { AdminUsuariosChatComponent } from './components/admin/admin-usuarios-chat/admin-usuarios-chat.component';
import { AdminMetaapiCuentasComponent } from './components/admin/admin-metaapi-cuentas/admin-metaapi-cuentas.component';
import { StrategyFactoryComponent } from './components/strategy-factory/strategy-factory.component';
import { CondicionesComponent } from './components/condiciones/condiciones.component';
import { BrokerOpDialogComponent } from './components/broker/broker-op-dialog/broker-op-dialog.component';
import { GestionListasComponent } from './components/gestion-listas/gestion-listas.component';
import { StockComponent } from './components/stock/stock.component';
import { NewsletterListComponent } from './components/newsletter/newsletter-list/newsletter-list.component';
import { NewsletterEditorComponent } from './components/newsletter/newsletter-editor/newsletter-editor.component';
import { NewsletterDetailComponent } from './components/newsletter/newsletter-detail/newsletter-detail.component';
import { NewsletterTemplatesComponent } from './components/newsletter/newsletter-templates/newsletter-templates.component';
import { SocialAccountsComponent } from './components/social/social-accounts/social-accounts.component';
import { SocialEditorComponent } from './components/social/social-editor/social-editor.component';
import { SocialListComponent } from './components/social/social-list/social-list.component';
import { SocialCommentsComponent } from './components/social-comments/social-comments.component';
import { ProfileComponent } from './components/profile/profile.component';

/**
 * /mercados, /mercados/:mercado y /mercados/:mercado/:activo en una sola ruta, para que Angular
 * reutilice AnalisisDataComponent al cambiar de mercado o activo en vez de recrearlo.
 * :mercado es "<idmercado>-<slug del nombre>" (p.ej. 12-nasdaq-100); sólo cuenta el id.
 */
export function mercadosMatcher(segments: UrlSegment[]): UrlMatchResult | null {
    if (!segments.length || segments[0].path !== 'mercados' || segments.length > 3) {
        return null;
    }
    const posParams: { [name: string]: UrlSegment } = {};
    if (segments[1]) { posParams['mercado'] = segments[1]; }
    if (segments[2]) { posParams['activo'] = segments[2]; }
    return { consumed: segments, posParams };
}

const routes: Routes = [
    { path: '', redirectTo: 'mercados', pathMatch: 'full' },
    { path: 'inicio', component: CryptoComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'models', component: ModelsComponent, canActivate: mapToCanActivate([AuthGuard]) },
    { path: 'portfolio-labs', component: DiversificadorComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'diversificador', redirectTo: 'portfolio-labs', pathMatch: 'full' },
    { path: 'gestor', component: GestorComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'broker', component: BrokerComponent, canActivate: mapToCanActivate([AuthGuard, BrokerTabGuard])},
    { path: 'iboauth', component: IboauthComponent, canActivate: mapToCanActivate([AuthGuard])},
    { matcher: mercadosMatcher, component: AnalisisDataComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'admin', component: AdminComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'admin-usuarios', component: AdminComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'admin-usuarios-chat', component: AdminUsuariosChatComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'admin-metaapi-cuentas', component: AdminMetaapiCuentasComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'dashboard', component: DashboardComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'listas', component: GestionListasComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: "seguimiento-carteras", component: SeguimientoCarterasComponent, canActivate: mapToCanActivate([AuthGuard]),},
    { path: "notifications", component: VernotificacionesComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'rastreador', component: RastreadorComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'strategyfactory', component: StrategyFactoryComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'algoritmobatch', redirectTo: 'strategyfactory', pathMatch: 'full' },
    { path: 'condiciones', component: CondicionesComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'newsletter', component: NewsletterListComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'newsletter/new', component: NewsletterEditorComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'newsletter/templates', component: NewsletterTemplatesComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'newsletter/:id/edit', component: NewsletterEditorComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'newsletter/:id', component: NewsletterDetailComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'social', component: SocialListComponent, canActivate: mapToCanActivate([AuthGuard]) },
    { path: 'social/accounts', component: SocialAccountsComponent, canActivate: mapToCanActivate([AuthGuard]) },
    { path: 'social/accounts/tiktok', component: SocialAccountsComponent , canActivate: mapToCanActivate([AuthGuard]) },
    { path: 'social/new', component: SocialEditorComponent, canActivate: mapToCanActivate([AuthGuard]) },
    { path: 'social/:id/edit', component: SocialEditorComponent, canActivate: mapToCanActivate([AuthGuard]) },
    { path: 'profile', component: ProfileComponent, canActivate: mapToCanActivate([AuthGuard]) },
    { path: 'login', component: LoginComponent },
    { path: 'logout', component: LogoutComponent },
    { path: 'reset-password', component: ResetPasswordComponent },
    { path: 'grafica-modelo/:data', component: GraficaModeloComponent},
    { path: 'confirmar', component: SuscripcionesConfirmarComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'broker-operations', component: BrokerOpDialogComponent, canActivate: mapToCanActivate([AuthGuard])},
    { path: 'stock', component: StockComponent },
    { path: 'stock/:symbol', component: StockComponent },
    {path: 'social/comments', component: SocialCommentsComponent},
    { path: '**', component: NotFoundComponent },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { scrollPositionRestoration: "enabled" }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}

